import {EventEmitter} from "@/utils/event-emitter";
import {InboxreaderPurchase} from "@/utils/purchase/inboxreader-purchase";
import {Purchase} from "@/models/base";
import {AWSFileService, PurchaseService} from "@/services";
import Vue from "vue";
import { InboxTypeState } from "./inbox-type-state";
import { InboxreaderLabel } from "@/utils/purchase/inboxreader-label";
import { InboxreaderService } from "@/services/inboxreader-service";
import { INBOX_SOURCES, InboxListState, InboxSource } from "./inbox-list-state";

const KEY_LOCAL_STORAGE_INBOX_VIEW = "inbox-view";
const KEY_LOCAL_STORAGE_INBOX_BAR_POSITION = "inbox-selection-bar-position";

// InboxView is how the documents are shown: as cards, as a list, or as a list
// next to the document itself.
export type InboxView = "grid" | "list" | "split";

function storedView():InboxView {
	let stored = localStorage.getItem(KEY_LOCAL_STORAGE_INBOX_VIEW);
	if (stored == "list" || stored == "split") return stored;
	return "grid";
}

export type InboxBarPosition = "top" | "bottom" | "pinned";

function storedBarPosition():InboxBarPosition {
	let stored = localStorage.getItem(KEY_LOCAL_STORAGE_INBOX_BAR_POSITION);
	if (stored == "top" || stored == "pinned") return stored;
	return "bottom";
}

// InboxTab is the tab the user is on. The tabs are nothing more than a filter
// on the sources of the one list.
export type InboxTab = "all" | "peppol" | "other";

export const TAB_SOURCES:Record<InboxTab, InboxSource[]> = {
	all: INBOX_SOURCES,
	peppol: ["peppol"],
	other: ["other"]
};

export class InboxState extends EventEmitter {

	vue:Vue;
	public preview: InboxreaderPurchase | null = null;
	public edit: Purchase | null = null;
	public archive:boolean = false;
	public search:string = "";
	// The chosen view is remembered per browser.
	public view:InboxView = storedView();
	public barPosition:InboxBarPosition = storedBarPosition();
	public tab:InboxTab = "all";
	public listState:InboxListState;
	public labels:InboxreaderLabel[] = [];
	public selectedLabelIds:number[] = [];


	public constructor(vue:Vue) {
		super();
		this.vue = vue;
		this.listState = new InboxListState(vue);
	}

	public async setTab(tab:InboxTab){
		if (this.tab == tab) return;
		this.tab = tab;
		this.clearSelection();
		this.listState.setSources(TAB_SOURCES[tab]);
		await this.fetchPurchases();
	}

	public setView(view:InboxView){
		this.view = view;
		localStorage.setItem(KEY_LOCAL_STORAGE_INBOX_VIEW, view);
		this.clearSelection();
	}

	public setBarPosition(position:InboxBarPosition){
		this.barPosition = position;
		localStorage.setItem(KEY_LOCAL_STORAGE_INBOX_BAR_POSITION, position);
	}

	public get selectedType():InboxTypeState {
		return this.listState;
	}

	public clearSelection(){
		this.selectedType.clearSelection();
	}

	public async doInitialFetch(){
		await this.listState.fetch(false);
	}

	public async fetchPurchases(){
		await this.selectedType.fetch(this.archive, this.search, this.selectedLabelIds);
	}

	public async fetchMore(){
		await this.selectedType.fetchMore(this.archive, this.search, this.selectedLabelIds);
	}

	public async loadLabels(){
		this.labels = await InboxreaderService.getLabels();
	}

	public toggleLabelFilter(labelId:number){
		let i = this.selectedLabelIds.indexOf(labelId);
		if (i == -1) {
			this.selectedLabelIds.push(labelId);
		} else {
			this.selectedLabelIds.splice(i, 1);
		}
	}

	// getSelectedFileIds returns the ids of every file in the current selection.
	public getSelectedFileIds():number[]{
		let ids:number[] = [];
		for (let p of this.selectedType.selection) {
			for (let f of p.Files) {
				if (f && ids.indexOf(f.ID) == -1) ids.push(f.ID);
			}
		}
		return ids;
	}

	// applyLabelToSelection assigns (or removes) a label for every file in the
	// current selection, then refreshes the list.
	public async applyLabelToSelection(labelId:number, assign:boolean = true){
		let fileIds = this.getSelectedFileIds();
		if (!fileIds.length) return;
		if (assign) {
			await InboxreaderService.assignLabel(labelId, fileIds);
		} else {
			await InboxreaderService.unassignLabel(labelId, fileIds);
		}
		await this.fetchPurchases();
	}

	public async generatePurchaseFromPeppolOrder(p:InboxreaderPurchase):Promise<Purchase>{
		if (!p.PeppolOrder) throw new Error("No peppol order");
		let purchase = await PurchaseService.purchaseFromOrder(p.PeppolOrder);
		purchase.InboxreaderPurchaseID = p.ID;
		purchase.InboxreaderPurchaseMessageIDs = p.Files.map(p=>p.ID);

		for (let file of p.Files) {
			if (!file.File) continue;
			let { Token, Name, ContentType } = file.File;
			if (!Name.toLowerCase().endsWith(".pdf") && file.File.ContentType.indexOf("pdf") != -1) {
				Name = Name + ".pdf";
			}
			const fetched = await AWSFileService.fetchFile(Token, Name, ContentType);
			purchase.UnprocessedFiles.push(fetched);
		}
		return purchase;
	}


	public createPurchase():Promise<Purchase>{
		return this.selectedType.createPurchase();
	}

}
